import { CSSProperties } from 'react';
import { ACCENT_BUTTON, COLORS, FONTS, GLASS, META } from '../../globalStyles';

// Shared base for the glass form fields (input + textarea differ only by resize).
const glassField: CSSProperties = {
  ...GLASS,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 0,
  color: COLORS.text,
  fontFamily: FONTS.body,
  fontSize: '0.9375rem',
  padding: '0.7rem 0.9rem',
  transition: 'border-color 0.15s',
  width: '100%',
  minWidth: 0,
};

export const styles: Record<string, CSSProperties> = {
  inner: {
    display: 'grid',
    gridTemplateColumns: '1fr 1.5fr',
    gap: '5rem',
    alignItems: 'start',
    marginTop: '3.5rem',
  },
  infoPara: {
    color: COLORS.muted,
    fontSize: '0.9375rem',
    lineHeight: 1.75,
    marginBottom: '2rem',
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.9rem',
  },
  detailItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    fontSize: '0.8rem',
    color: COLORS.muted,
    fontFamily: FONTS.body,
  },
  dot: {
    width: '6px',
    height: '6px',
    borderRadius: 0,
    background: COLORS.accent,
    transform: 'rotate(45deg)',
    flexShrink: 0,
  },
  link: {
    color: COLORS.accent,
    textDecoration: 'none',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.1rem',
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1.1rem',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    minWidth: 0, // let the field shrink inside the grid instead of forcing overflow
  },
  label: {
    ...META,
    fontSize: '0.66rem',
    color: COLORS.label,
  },
  input: glassField,
  textarea: { ...glassField, resize: 'none' as const },
  // Applied to a field that failed client-side validation.
  fieldInvalid: { borderColor: COLORS.error },
  fieldErrorText: {
    ...META,
    fontSize: '0.6rem',
    color: COLORS.error,
    marginTop: '0.15rem',
  },
  submit: {
    ...META,
    ...ACCENT_BUTTON,
    padding: '0.85rem 1.9rem',
    fontSize: '0.72rem',
    alignSelf: 'flex-start',
    marginTop: '0.5rem',
  },
  // Visually hidden honeypot — off-screen, untabbable, hidden from a11y tree.
  // A real user never sees it; bots that fill it get silently dropped server-side.
  honeypot: {
    position: 'absolute',
    left: '-9999px',
    width: '1px',
    height: '1px',
    opacity: 0,
    overflow: 'hidden',
  },
  // Send-result feedback line, shown beneath the button.
  feedback: {
    ...META,
    fontSize: '0.66rem',
    lineHeight: 1.6,
    marginTop: '0.9rem',
    alignSelf: 'flex-start',
  },
  feedbackError: { color: COLORS.error },
  feedbackSuccess: { color: COLORS.accent },
};
